import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  useAskMutations,
  useAskRequest,
  useConversations,
  useGetTenants,
  useThread,
} from '../../hooks';
import type { IAskConversation, IAskRequest, IAskStep } from '../../types';
import { closeFences, renderAnswer, splitAnswer, type CodeBlock } from './answer';
import { newConversationId, relative, seconds } from './format';
import styles from './Ask.module.scss';

const TENANT_KEY = 'nexus-ui.ask.tenant';
const CHARS_PER_SECOND = 90;
const ACTIVE_KEY = 'nexus-ui.ask.active';

const EXAMPLES = [
  'Which venues have no price band in any scheme?',
  'How many publishes failed in the last 7 days, and for which venues?',
  'Which addons are installed, for which brands, and how many instances does each have?',
  'Show every price record for one item with a Deliveroo price, labelled by type.',
];

const readStored = (key: string): string => {
  try {
    return globalThis.localStorage?.getItem(key) ?? '';
  } catch {
    return '';
  }
};

const store = (key: string, value: string): void => {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // storage unavailable; the page still works for this visit
  }
};

const StepRow = ({ step }: { step: IAskStep }) => {
  const sql = typeof step.input.sql === 'string' ? step.input.sql : null;
  const purpose = typeof step.input.purpose === 'string' ? step.input.purpose : null;
  const table = typeof step.input.table === 'string' ? step.input.table : null;
  const summary =
    purpose ?? table ?? (typeof step.input.pattern === 'string' ? step.input.pattern : null);
  return (
    <div className={styles.step}>
      <div className={styles.stepHead}>
        <span className={styles.stepTool}>{step.tool}</span>
        {summary ? <span>{summary}</span> : null}
        <span>{step.elapsed_ms} ms</span>
        {step.ok ? null : <span className={styles.stepFailed}>failed</span>}
      </div>
      {sql ? <pre>{sql}</pre> : null}
      {!step.ok || !sql ? <pre>{step.output}</pre> : null}
    </div>
  );
};

const Feedback = ({ request }: { request: IAskRequest }) => {
  const { feedback } = useAskMutations();
  const current = request.feedback?.rating ?? null;
  const send = (rating: 'up' | 'down') => {
    if (current === rating) {
      return;
    }
    const comment =
      rating === 'down' ? window.prompt('What was wrong? (optional)') ?? undefined : undefined;
    feedback.mutate({ id: request.id, input: { rating, comment } });
  };
  return (
    <div className={styles.feedback} aria-label="Was this answer useful?">
      <button
        type="button"
        aria-pressed={current === 'up'}
        disabled={feedback.isLoading}
        onClick={() => send('up')}
      >
        Useful
      </button>
      <button
        type="button"
        aria-pressed={current === 'down'}
        disabled={feedback.isLoading}
        onClick={() => send('down')}
      >
        Wrong
      </button>
    </div>
  );
};

/**
 * Reveals text progressively, like typing, when `animate` is on. The answer arrives whole from the API, so this is
 * presentation only; history that loads already answered is shown at once.
 */
const useTypewriter = (text: string | null, animate: boolean): string => {
  const [count, setCount] = useState(() => (animate ? 0 : text?.length ?? 0));
  useEffect(() => {
    if (!text) {
      setCount(0);
      return undefined;
    }
    if (!animate) {
      setCount(text.length);
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const target = Math.min(text.length, Math.floor(((now - started) / 1000) * CHARS_PER_SECOND));
      setCount(target);
      if (target < text.length) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [text, animate]);
  return text ? text.slice(0, count) : '';
};

const Queries = ({ code }: { code: CodeBlock[] }) =>
  code.length === 0 ? null : (
    <>
      {code.map((block, index) => (
        <pre
          key={`${index}-${block.code.length}-${block.code.slice(0, 24)}`}
          className={styles.code}
        >
          <code>{block.code}</code>
        </pre>
      ))}
    </>
  );

interface BehindTheScenesProps {
  request: IAskRequest;
  code: CodeBlock[];
}

/** Everything that is not the answer: the queries the model wrote, each tool step, and the cost line. Collapsed. */
const BehindTheScenes = ({ request, code }: BehindTheScenesProps) => {
  const steps = request.steps.length;
  if (steps === 0 && code.length === 0) {
    return null;
  }
  const tokens = request.usage ? request.usage.input_tokens + request.usage.output_tokens : null;
  const took = seconds(request.started_at, request.finished_at);
  return (
    <details className={styles.working}>
      <summary>
        How this was answered · {steps} {steps === 1 ? 'step' : 'steps'}
        {code.length ? ` · ${code.length} ${code.length === 1 ? 'query' : 'queries'}` : ''}
      </summary>
      <Queries code={code} />
      {request.steps.map((step) => (
        <StepRow key={step.index} step={step} />
      ))}
      {request.model ? (
        <p className={styles.metaLine}>
          {request.model}
          {request.usage ? ` · ${request.usage.model_calls} calls · ${tokens} tokens` : ''}
          {took ? ` · ${took}` : ''}
        </p>
      ) : null}
    </details>
  );
};

const AnswerBody = ({ request, animate }: { request: IAskRequest; animate: boolean }) => {
  const split = useMemo(
    () => (request.answer ? splitAnswer(request.answer) : { prose: '', code: [] }),
    [request.answer],
  );
  const typed = useTypewriter(request.status === 'done' ? split.prose : null, animate);
  if (request.status === 'failed') {
    return (
      <>
        <p className={styles.failed}>Could not answer: {request.error ?? 'unknown error'}</p>
        <BehindTheScenes request={request} code={[]} />
      </>
    );
  }
  if (request.status === 'done' && request.answer) {
    const complete = typed.length >= split.prose.length;
    return (
      <>
        <div className={complete ? undefined : styles.typing}>
          {renderAnswer(closeFences(typed))}
        </div>
        {complete ? (
          <>
            <BehindTheScenes request={request} code={split.code} />
            <div className={styles.meta}>
              <Feedback request={request} />
            </div>
          </>
        ) : null}
      </>
    );
  }
  const stepsSoFar = `${request.steps.length} ${request.steps.length === 1 ? 'step' : 'steps'}`;
  const label = request.status === 'queued' ? 'Thinking' : `Working, ${stepsSoFar} so far`;
  return (
    <output className={styles.status}>
      <span className={styles.dot} aria-hidden="true" />
      {label}
    </output>
  );
};

/** One question and its answer. Polls while running; an answer that arrives during this visit types itself out. */
const Turn = ({ initial }: { initial: IAskRequest }) => {
  const live = useAskRequest(initial);
  const request = live.data ?? initial;
  const sawPending = useRef(initial.status !== 'done' && initial.status !== 'failed');
  return (
    <article className={styles.turn} aria-label={request.question}>
      <div className={styles.question}>{request.question}</div>
      <div className={styles.answer}>
        <AnswerBody request={request} animate={sawPending.current} />
      </div>
    </article>
  );
};

interface SidebarProps {
  conversations: IAskConversation[];
  activeId: string | null;
  draftId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (conversation: IAskConversation) => void;
}

const Sidebar = ({ conversations, activeId, draftId, onSelect, onNew, onDelete }: SidebarProps) => (
  <aside className={styles.sidebar} aria-label="Chats">
    <button
      type="button"
      className={styles.newChat}
      onClick={onNew}
      disabled={draftId !== null && draftId === activeId}
    >
      New chat
    </button>
    <ul className={styles.chatList}>
      {draftId ? (
        <li>
          <button
            type="button"
            className={styles.chatItem}
            aria-current={activeId === draftId ? 'true' : undefined}
            onClick={() => onSelect(draftId)}
          >
            <span className={styles.chatTitle}>New chat</span>
            <span className={styles.chatMeta}>not started</span>
          </button>
        </li>
      ) : null}
      {conversations.map((conversation) => (
        <li key={conversation.id} className={styles.chatRow}>
          <button
            type="button"
            className={styles.chatItem}
            aria-current={activeId === conversation.id ? 'true' : undefined}
            onClick={() => onSelect(conversation.id)}
          >
            <span className={styles.chatTitle}>{conversation.title}</span>
            <span className={styles.chatMeta}>
              {conversation.turns} {conversation.turns === 1 ? 'question' : 'questions'} ·{' '}
              {relative(conversation.updated_at)}
            </span>
          </button>
          <button
            type="button"
            className={styles.chatDelete}
            aria-label={`Delete chat ${conversation.title}`}
            title="Delete chat"
            onClick={() => onDelete(conversation)}
          >
            ×
          </button>
        </li>
      ))}
    </ul>
  </aside>
);

export const Ask = () => {
  const { data: tenants = [] } = useGetTenants();
  const [tenant, setTenant] = useState<string>(() => readStored(TENANT_KEY));
  const [draft, setDraft] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [sessionRequests, setSessionRequests] = useState<IAskRequest[]>([]);
  const { ask, removeConversation } = useAskMutations();
  const conversations = useConversations(tenant || null);
  const threadRef = useRef<HTMLDivElement>(null);

  const tenantNames = useMemo(
    () => tenants.map((item) => item.name).sort((a, b) => a.localeCompare(b)),
    [tenants],
  );
  const list = useMemo(() => conversations.data ?? [], [conversations.data]);
  const activeExists = list.some((item) => item.id === activeId);
  const thread = useThread(activeId, activeExists);

  useEffect(() => {
    if (!tenant && tenantNames.length > 0) {
      setTenant(tenantNames[0]);
    }
  }, [tenant, tenantNames]);

  useEffect(() => {
    if (tenant) {
      store(TENANT_KEY, tenant);
    }
  }, [tenant]);

  // A draft is a chat that has not reached the server yet; once the server lists it, it is an ordinary chat.
  const pendingDraftId = draftId && !list.some((item) => item.id === draftId) ? draftId : null;

  // Pick a chat only when nothing is selected: the remembered one for this tenant, else the newest, else a draft.
  useEffect(() => {
    if (!tenant || !conversations.isSuccess || activeId) {
      return;
    }
    const remembered = readStored(`${ACTIVE_KEY}.${tenant}`);
    const candidate = list.some((item) => item.id === remembered)
      ? remembered
      : list[0]?.id ?? null;
    if (candidate) {
      setActiveId(candidate);
    } else {
      const id = newConversationId();
      setDraftId(id);
      setActiveId(id);
    }
  }, [tenant, conversations.isSuccess, list, activeId]);

  useEffect(() => {
    if (tenant && activeId) {
      store(`${ACTIVE_KEY}.${tenant}`, activeId);
    }
  }, [tenant, activeId]);

  const changeTenant = (next: string) => {
    setTenant(next);
    setActiveId(null);
    setDraftId(null);
    setSessionRequests([]);
  };

  const startNewChat = () => {
    if (pendingDraftId) {
      setActiveId(pendingDraftId);
      return;
    }
    const id = newConversationId();
    setDraftId(id);
    setActiveId(id);
  };

  const deleteChat = (conversation: IAskConversation) => {
    if (
      !globalThis.confirm(`Delete "${conversation.title}"? This removes its questions and answers.`)
    ) {
      return;
    }
    removeConversation.mutate(conversation.id, {
      onSuccess: () => {
        setSessionRequests((current) =>
          current.filter((item) => item.conversation_id !== conversation.id),
        );
        if (activeId !== conversation.id) {
          return;
        }
        // Move to the draft if there is one, else the next chat, else a fresh draft; never wait on the stale list.
        const next = pendingDraftId ?? list.find((item) => item.id !== conversation.id)?.id ?? null;
        if (next) {
          setActiveId(next);
        } else {
          const id = newConversationId();
          setDraftId(id);
          setActiveId(id);
        }
      },
    });
  };

  const turns = useMemo(() => {
    const fromServer = (thread.data ?? []).filter((item) => item.conversation_id === activeId);
    const known = new Set(fromServer.map((item) => item.id));
    const extra = sessionRequests.filter(
      (item) => item.conversation_id === activeId && !known.has(item.id),
    );
    return [...fromServer, ...extra].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [thread.data, sessionRequests, activeId]);

  useEffect(() => {
    const node = threadRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [turns.length]);

  const canSend = Boolean(tenant) && Boolean(activeId) && draft.trim().length > 0 && !ask.isLoading;

  const send = () => {
    if (!canSend || !activeId) {
      return;
    }
    const question = draft.trim();
    setDraft('');
    ask.mutate(
      { tenant, question, conversationId: activeId },
      {
        onSuccess: ({ data }) => {
          setSessionRequests((current) => [...current, data]);
        },
      },
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const active = list.find((item) => item.id === activeId);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Ask</h1>
          <p className={styles.subtitle}>
            Questions about one tenant, answered from its database. Read-only; every query is shown.
          </p>
        </div>
        <label className={styles.tenantPicker}>
          <span>Tenant</span>
          <select
            value={tenant}
            onChange={(event) => changeTenant(event.target.value)}
            aria-label="Tenant"
          >
            {tenant === '' ? <option value="">Choose a tenant</option> : null}
            {tenantNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={styles.body}>
        <Sidebar
          conversations={list}
          activeId={activeId}
          draftId={pendingDraftId}
          onSelect={setActiveId}
          onNew={startNewChat}
          onDelete={deleteChat}
        />
        <section className={styles.chat} aria-label={active?.title ?? 'New chat'}>
          <div className={styles.thread} ref={threadRef}>
            {turns.length === 0 ? (
              <div className={styles.empty}>
                <h2>Ask about {tenant || 'a tenant'}</h2>
                <p>
                  The assistant reads the tenant schema with a read-only role and shows the SQL
                  behind every answer. Follow-up questions build on earlier answers in the same
                  chat.
                </p>
                <ul>
                  {EXAMPLES.map((example) => (
                    <li key={example}>
                      <button
                        type="button"
                        className={styles.exampleButton}
                        onClick={() => setDraft(example)}
                      >
                        {example}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              turns.map((item) => <Turn key={item.id} initial={item} />)
            )}
          </div>

          <div className={styles.composer}>
            <textarea
              aria-label="Question"
              placeholder={tenant ? `Ask about ${tenant}…` : 'Choose a tenant first'}
              value={draft}
              rows={2}
              disabled={!tenant || !activeId}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
            />
            <button type="button" disabled={!canSend} onClick={send}>
              {ask.isLoading ? 'Sending…' : 'Ask'}
            </button>
          </div>
          {ask.error ? (
            <p className={styles.failed}>
              {(ask.error as { message?: string }).message ?? 'Could not send'}
            </p>
          ) : null}
          <p className={styles.hint}>Enter to send, Shift+Enter for a new line.</p>
        </section>
      </div>
    </div>
  );
};
